import {FC, useState, ReactNode, HTMLAttributes, useEffect} from 'react'
import {useForm, Controller, FormProvider} from 'react-hook-form'
import {useHistory} from 'react-router-dom'
import {useSelector} from 'hooks/redux'

import {Textarea} from 'jbc-front/components/presenters/form/Textarea'
import {Label, Required} from 'jbc-front/components/presenters/form/Label'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Spacer} from 'jbc-front/components/presenters/Spacer'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Select, Option} from 'jbc-front/components/presenters/form/Select'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import Button from 'jbc-front/components/Button'
import {Section} from 'jbc-front/components/Form'
import {Modal} from 'jbc-front/components/presenters/ui/Modal'
import {ButtonRow} from 'jbc-front/components/presenters/layout/ButtonRow'

import {yupResolver} from '@hookform/resolvers/yup'
import {CustomEmployeeFieldGroup, convertFieldsToCustomValues} from '../../create/query'
import {AlertType, ConvertQueryAlertTypeToFormData} from '../query'
import {isChangeNotificationDate} from '../mutation'
import {AdminMailBody} from '../../create/parts/AdminMailBody'
import {EmployeeMailBody} from '../../create/parts/EmployeeMailBody'
import {OfficeCheckboxField} from '../../create/parts/OfficeCheckboxField'
import {schema, Schema} from '../../create/schema'
import {FormField} from 'components/forms/FormField'
import {FormSection} from 'components/forms/FormSection'
import {
  wrapperSectionStyle,
  basicBaseDateTypeSelectOptions,
  reasonTypeSelectOptions,
  colors,
  toColor,
  dateUnitOptionsFilter,
  alertTargetSelectOptions,
} from '../../create/parts/formUtils'
import {BASIC_BASE_DATE_TYPE_VALUES} from 'consts/alertCustom'
import {isOfficeAdminSelector} from 'libs/redux'

import styles from '../../create/parts/AlertTypeCreateForm.scss'
import {OfficeForAdmin} from 'types/api/offices/office'

type DisabledReasonProps = {
  children: ReactNode
}

const DisabledReason = ({children}: DisabledReasonProps) => (
  <div className={styles.disabledReason}>
    <div className={styles.content}>{children}</div>
  </div>
)

type ItemProps = {
  children: ReactNode
  disabled: boolean
  disabledReason?: ReactNode
} & HTMLAttributes<HTMLDivElement>

const Item = ({children, disabled, disabledReason = null, ...rest}: ItemProps) => {
  return (
    <div className={disabled ? styles.disabledItem : styles.item} {...rest}>
      {children}
      {disabled && !!disabledReason && <DisabledReason>{disabledReason}</DisabledReason>}
    </div>
  )
}

const createDefaultValues = (
  alertType: AlertType,
  isOfficeAdmin: boolean,
  baseDateTypeSelectOptions: Option[],
  currentOffices: OfficeForAdmin[]
) => {
  const base = ConvertQueryAlertTypeToFormData(
    alertType,
    baseDateTypeSelectOptions,
    reasonTypeSelectOptions,
    alertTargetSelectOptions
  )
  let selectedAllOffices = base.selectedAllOffices
  let selectedOfficeIds = base.selectedOfficeIds ?? []

  if (isOfficeAdmin) {
    const userOfficeIds = currentOffices.map((office) => String(office.id))
    const userOfficesAllIncluded = userOfficeIds.every((id) => selectedOfficeIds.includes(id))
    if (base.selectedAllOffices && (userOfficesAllIncluded || selectedOfficeIds.length === 0)) {
      selectedOfficeIds = []
    } else {
      selectedAllOffices = false
    }
  } else {
    if (base.selectedAllOffices && selectedOfficeIds.length > 0) {
      selectedAllOffices = false
    }
  }

  return {...base, selectedAllOffices, selectedOfficeIds}
}

type AlertTypeEditFormProps = {
  onSubmit: (data: Schema, isDestroy?: boolean) => Promise<void>
  customFields: CustomEmployeeFieldGroup[]
  loading: boolean
  alertType: AlertType | undefined
}

export const AlertTypeEditForm: FC<AlertTypeEditFormProps> = ({onSubmit, loading, customFields, alertType}) => {
  const history = useHistory()
  const baseDateTypeSelectOptions: Option[] = [
    ...basicBaseDateTypeSelectOptions,
    ...convertFieldsToCustomValues(customFields),
  ]
  const isOfficeAdmin = useSelector(isOfficeAdminSelector)
  const currentOffices = useSelector((state) => state.session.currentUser?.current_offices || [])

  const editable = alertType?.editable
  const defaultValues = alertType
    ? createDefaultValues(alertType, isOfficeAdmin, baseDateTypeSelectOptions, currentOffices)
    : undefined

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  })
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    control,
    trigger,
    formState: {errors, isValid},
  } = methods

  useEffect(() => {
    const selectedAllOffices = getValues('selectedAllOffices')
    const selectedOfficeIds = getValues('selectedOfficeIds')
    if (selectedAllOffices === false && (!selectedOfficeIds || selectedOfficeIds.length === 0)) {
      trigger('selectedOfficeIds')
    }
  }, [getValues, trigger])

  const selectedBaseDateType = watch('baseDateType')
  const adminMailActive = watch('adminMailActive')
  const employeeMailActive = watch('employeeMailActive')

  const isAgeOrBirthday = ['age', 'birthday'].includes(selectedBaseDateType?.value)

  const filteredDateUnitOptions = dateUnitOptionsFilter(selectedBaseDateType.value)

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)

  const handleCloseModal = () => setIsModalOpen(false)
  const handleOpenModal = () => setIsModalOpen(true)

  const handleSave = (data: Schema) => {
    if (isChangeNotificationDate(defaultValues, data)) {
      handleOpenModal()
    } else {
      onSubmit(data)
    }
  }

  const handleModalSave = async () => {
    await onSubmit(getValues(), true)
    setIsModalOpen(false)
  }

  const disabledReason = '編集権限がありません。'

  return (
    <div>
      <form onSubmit={handleSubmit(handleSave)} className={styles.FormAlertCustomization}>
        <Section title="アラート設定" style={wrapperSectionStyle}>
          <FormSection>
            <FormField>
              <Label>アラートカラー</Label>
              <Spacer direction="y" size={8} />
              <RadioContainer className={styles.radioColorContainer}>
                <Controller
                  name="color"
                  control={control}
                  render={({field}) => (
                    <>
                      {colors.map((color) => {
                        const isChecked = field.value === color
                        return (
                          <Item disabled={!editable} disabledReason={disabledReason} key={color}>
                            <Radio
                              key={color}
                              value={color}
                              checked={isChecked}
                              onChange={() => field.onChange(color)}
                              className={!editable && isChecked ? 'disabled-checked-radio' : undefined}
                              disabled={!editable}
                            >
                              <span className={styles.colorOption} style={{backgroundColor: toColor(color)}} />
                            </Radio>
                          </Item>
                        )
                      })}
                    </>
                  )}
                />
              </RadioContainer>
            </FormField>
            <FormField>
              <FormField.LabelContainer>
                <Label>
                  アラート名
                  <Spacer direction="x" size={8} />
                  <Required />
                </Label>
              </FormField.LabelContainer>
              <Item disabled={!editable} disabledReason={disabledReason}>
                <Input {...register('name')} disabled={!editable} />
              </Item>
              <FormField.ErrorContainer>
                {errors.name?.message && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </FormField.ErrorContainer>
            </FormField>

            <FormField>
              <FormField.LabelContainer>
                <Label>説明</Label>
              </FormField.LabelContainer>
              <Item disabled={!editable} disabledReason={disabledReason}>
                <Textarea rows={4} {...register('description')} disabled={!editable} />
              </Item>
              <FormField.ErrorContainer>
                {errors.description?.message && <ErrorMessage>{errors.description.message}</ErrorMessage>}
              </FormField.ErrorContainer>
            </FormField>

            <Section title="通知基準" style={wrapperSectionStyle}>
              <div className={styles.baseDateContainer}>
                <div>
                  <FormField.LabelContainer>
                    <Label>
                      基準日
                      <Spacer direction="x" size={8} />
                      <Required />
                    </Label>
                  </FormField.LabelContainer>
                  <div className={styles.baseDateArea}>
                    <div className={styles.baseDateSelect}>
                      <Item disabled={!editable} disabledReason={disabledReason}>
                        <Controller
                          name="baseDateType"
                          control={control}
                          render={({field}) => {
                            const selectedOption = baseDateTypeSelectOptions.find(
                              (option) => option.value === field.value?.value
                            )
                            return (
                              <Select
                                options={baseDateTypeSelectOptions}
                                value={selectedOption}
                                onChange={(val: Option) => {
                                  field.onChange(val)
                                  trigger('offset')
                                  trigger('days')
                                  trigger('dateUnit')
                                  trigger('alertTargets')
                                }}
                                noOptionsMessage={() => 'データが見つかりません'}
                                disabled={!editable}
                              />
                            )
                          }}
                        />
                      </Item>
                    </div>
                    {['age', 'service_years'].includes(selectedBaseDateType.value) && (
                      <div>
                        <Item disabled={!editable} disabledReason={disabledReason}>
                          <Input {...register('offset')} disabled={!editable} />
                        </Item>
                        <span>
                          {selectedBaseDateType.value === 'age'
                            ? '歳'
                            : selectedBaseDateType.value === 'service_years'
                              ? '年'
                              : ''}
                        </span>
                      </div>
                    )}

                    {['absence_start_day', 'absence_end_day'].includes(selectedBaseDateType.value) && (
                      <div className={styles.reasonTypeSelect}>
                        <Item disabled={!editable} disabledReason={disabledReason}>
                          <Controller
                            name="reasonType"
                            control={control}
                            render={({field}) => (
                              <Select
                                {...register('reasonType')}
                                options={reasonTypeSelectOptions}
                                value={field.value}
                                onChange={(val: Option) => {
                                  field.onChange(val)
                                }}
                                noOptionsMessage={() => 'データが見つかりません'}
                                disabled={!editable}
                              />
                            )}
                          />
                        </Item>
                      </div>
                    )}

                    <div>
                      <Item disabled={!editable} disabledReason={disabledReason}>
                        <Input className={styles.inputDays} {...register('days')} disabled={!editable} />
                      </Item>

                      <div className={styles.dateUnitField}>
                        <Item disabled={!editable} disabledReason={disabledReason}>
                          <Controller
                            name="dateUnit"
                            control={control}
                            render={({field}) => (
                              <Select
                                options={filteredDateUnitOptions}
                                value={filteredDateUnitOptions?.find((option: Option) => option.value === field.value)}
                                onChange={(val: Option) => {
                                  field.onChange(val.value)
                                  trigger('days')
                                }}
                                noOptionsMessage={() => 'データが見つかりません'}
                                disabled={!editable}
                              />
                            )}
                          />
                        </Item>
                      </div>

                      <RadioContainer className={styles.radioContainer}>
                        <Item disabled={!editable} disabledReason={disabledReason}>
                          <Radio {...register('isBefore')} value="Before" disabled={!editable}>
                            前
                          </Radio>
                        </Item>
                        <Item disabled={!editable} disabledReason={disabledReason}>
                          <Radio {...register('isBefore')} value="After" disabled={!editable}>
                            後
                          </Radio>
                        </Item>
                      </RadioContainer>
                    </div>
                  </div>
                </div>
                <div>
                  <FormField.LabelContainer>
                    <Label>
                      対象者
                      <Spacer direction="x" size={8} />
                      {isAgeOrBirthday && <Required />}
                    </Label>
                  </FormField.LabelContainer>
                  <div className={styles.alertTargetArea}>
                    <div className={styles.alertTargetCheckBoxList}>
                      <Controller
                        name="alertTargets"
                        control={control}
                        render={({field}) => {
                          const selectedBaseDateType = watch('baseDateType')
                          const isCustomBaseDateType = !BASIC_BASE_DATE_TYPE_VALUES.includes(
                            selectedBaseDateType?.value
                          )

                          const value: Option[] = isAgeOrBirthday
                            ? field.value
                            : isCustomBaseDateType
                              ? []
                              : [alertTargetSelectOptions[0]!]

                          const handleCheckAlertTarget = (option: Option, checked: boolean) => {
                            if (!isAgeOrBirthday) return

                            let newValue = field.value || []
                            if (checked) {
                              newValue = [...newValue, option]
                            } else {
                              newValue = newValue.filter((v: Option) => v.value !== option.value)
                            }
                            field.onChange(newValue)
                          }

                          return (
                            <div className={styles.alertTargetCheckBoxList}>
                              {alertTargetSelectOptions.map((option) => {
                                const checked = value?.some((v: Option) => v.value === option.value)

                                return (
                                  <div key={option.value}>
                                    <Item disabled={!editable} disabledReason={disabledReason}>
                                      <Checkbox
                                        disabled={!isAgeOrBirthday || !editable}
                                        checked={checked}
                                        onChange={(e) => handleCheckAlertTarget(option, e.target.checked)}
                                      >
                                        {option.label}
                                      </Checkbox>
                                    </Item>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>

                {errors.alertTargets?.message && <ErrorMessage>{errors.alertTargets.message}</ErrorMessage>}
                {errors.offset?.message && <ErrorMessage>{errors.offset.message}</ErrorMessage>}
                {errors.days?.message && <ErrorMessage>{errors.days.message}</ErrorMessage>}
                {errors.dateUnit?.message && <ErrorMessage>{errors.dateUnit.message}</ErrorMessage>}
              </div>
            </Section>

            <Section title="通知先" style={wrapperSectionStyle}>
              <div className={styles.checkBoxList}>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <Checkbox {...register('adminMailActive')} disabled={!editable}>
                    管理者にメール通知
                  </Checkbox>
                </Item>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <Checkbox {...register('employeeMailActive')} disabled={!editable}>
                    従業員にメール通知
                  </Checkbox>
                </Item>
              </div>
            </Section>

            <Section title="適用範囲" style={wrapperSectionStyle}>
              <div className={styles.baseOfficeContainer}>
                <FormField>
                  <FormField.LabelContainer>
                    <Label>
                      適用範囲
                      <Spacer direction="x" size={8} />
                      <Required />
                    </Label>
                  </FormField.LabelContainer>
                  <RadioContainer>
                    <Controller
                      name="selectedAllOffices"
                      control={control}
                      render={({field}) => (
                        <>
                          <Item disabled={!editable} disabledReason={disabledReason}>
                            <Radio
                              value="true"
                              checked={field.value === true}
                              onChange={() => field.onChange(true)}
                              disabled={!editable}
                            >
                              {isOfficeAdmin ? '全事業所' : '全社'}
                            </Radio>
                          </Item>
                          <Item disabled={!editable} disabledReason={disabledReason}>
                            <Radio
                              value="false"
                              checked={field.value === false}
                              onChange={() => {
                                field.onChange(false)
                                trigger('selectedOfficeIds')
                              }}
                              disabled={!editable}
                            >
                              適用事業所を選ぶ
                            </Radio>
                          </Item>
                        </>
                      )}
                    />
                  </RadioContainer>
                </FormField>
                {editable && !watch('selectedAllOffices') && (
                  <FormProvider {...methods}>
                    <OfficeCheckboxField
                      name="selectedOfficeIds"
                      label="適用事業所"
                      filterByCurrentOffices={false}
                      required
                      error={errors.selectedOfficeIds?.message}
                    />
                  </FormProvider>
                )}
              </div>
            </Section>

            <Item disabled={!editable} disabledReason={disabledReason}>
              <Checkbox {...register('active')} disabled={!editable}>
                このアラート設定を有効にする
              </Checkbox>
            </Item>
          </FormSection>
        </Section>

        {adminMailActive && (
          <Section title="メール設定（管理者）" style={wrapperSectionStyle}>
            <FormSection>
              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    件名
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <Input {...register('adminMailSubject')} disabled={!editable} />
                </Item>
                <FormField.ErrorContainer>
                  {errors.adminMailSubject?.message && <ErrorMessage>{errors.adminMailSubject.message}</ErrorMessage>}
                </FormField.ErrorContainer>
              </FormField>

              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    本文
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <FormField.ErrorContainer>
                  {errors.adminMailBody?.message && <ErrorMessage>{errors.adminMailBody.message}</ErrorMessage>}
                </FormField.ErrorContainer>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <AdminMailBody name="adminMailBody" control={control} disabled={!editable} />
                </Item>
              </FormField>
            </FormSection>
          </Section>
        )}

        {employeeMailActive && (
          <Section title="メール設定（従業員）" style={wrapperSectionStyle}>
            <FormSection>
              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    件名
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <Input {...register('employeeMailSubject')} disabled={!editable} />
                </Item>
                <FormField.ErrorContainer>
                  {errors.employeeMailSubject?.message && (
                    <ErrorMessage>{errors.employeeMailSubject.message}</ErrorMessage>
                  )}
                </FormField.ErrorContainer>
              </FormField>

              <FormField>
                <FormField.LabelContainer>
                  <Label>
                    本文
                    <Spacer direction="x" size={8} />
                    <Required />
                  </Label>
                </FormField.LabelContainer>
                <FormField.ErrorContainer>
                  {errors.employeeMailBody?.message && <ErrorMessage>{errors.employeeMailBody.message}</ErrorMessage>}
                </FormField.ErrorContainer>
                <Item disabled={!editable} disabledReason={disabledReason}>
                  <EmployeeMailBody name="employeeMailBody" control={control} disabled={!editable} />
                </Item>
              </FormField>
            </FormSection>
          </Section>
        )}

        {editable && (
          <div className={styles.bottomButtonsArea}>
            <Button onClick={() => history.push('/settings/alert_custom')} disabled={loading}>
              キャンセル
            </Button>
            <Button onClick={handleSubmit(handleSave)} primary disabled={loading || !isValid}>
              保存
            </Button>
          </div>
        )}
      </form>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <Modal.Header onClose={handleCloseModal}>変更の確認</Modal.Header>
        <Modal.Body>
          通知基準の設定を変更すると、変更前のアラートが削除され、変更後の内容でアラートが再設定されます。
          <br />
          <span className={styles.attention}>設定を変更しますか？</span>
        </Modal.Body>
        <Modal.Footer>
          <ButtonRow>
            <Button onClick={handleCloseModal}>いいえ</Button>
            <Button primary onClick={handleModalSave} disabled={loading}>
              はい
            </Button>
          </ButtonRow>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
